/**
 * Shared default positions for floating panels so they don't overlap
 * on first open / after layout version bump.
 *
 * Layout sketch (desktop):
 *  ┌──────────────────────────────────────────────┐
 *  │ header                                       │
 *  │ [Layers docked left]              [Bg •] [ ] │
 *  │                                    tools rail│
 *  │                         [Details]            │
 *  │                                              │
 *  │ [Info]                              [Tools+] │
 *  └──────────────────────────────────────────────┘
 */

export const PANEL_LAYOUT_VERSION = 5;
export const PANEL_LAYOUT_VERSION_KEY = 'mdv-panel-layout-version';

export function ensurePanelLayoutVersion() {
  try {
    const current = localStorage.getItem(PANEL_LAYOUT_VERSION_KEY);
    if (String(current) === String(PANEL_LAYOUT_VERSION)) return;
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k) keys.push(k);
    }
    keys.forEach((k) => {
      if (k.startsWith('mdv-panel-pos:')) localStorage.removeItem(k);
      if (k.startsWith('mdv-unified-panel-dock:')) localStorage.removeItem(k);
      if (k === 'mdv-submenu-layout') localStorage.removeItem(k);
      if (k.startsWith('mdv-session:')) {
        try {
          const data = JSON.parse(localStorage.getItem(k));
          if (data && typeof data === 'object') {
            localStorage.setItem(
              k,
              JSON.stringify({
                ...data,
                panel: {
                  ...(data.panel && typeof data.panel === 'object' ? data.panel : {}),
                  docked: true,
                  dockSide: 'left',
                },
              })
            );
          }
        } catch {
          /* ignore */
        }
      }
    });
    localStorage.setItem(PANEL_LAYOUT_VERSION_KEY, String(PANEL_LAYOUT_VERSION));
  } catch {
    /* ignore */
  }
}

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/** @returns {{ left: number, top: number }} */
export function defaultPanelPosition(id) {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const h = typeof window !== 'undefined' ? window.innerHeight : 800;
  const header = 40;
  const margin = 12;
  const fabSize = 56;
  const measureW = 52;
  const measureH = 300;
  const drawW = 160;
  const panelW = 320;
  const detailsW = 288;
  const bgSize = 40;

  const map = {
    // Top-left — draw tools
    'scribble-dock': {
      left: margin,
      top: header + margin,
    },
    // Left, beside draw (or same top if draw closed)
    'unified-panel': {
      left: margin + drawW + margin,
      top: header + margin,
    },
    // Top-right — background colour dot
    'scene-bg': {
      left: clamp(w - bgSize - margin, margin, w - bgSize - margin),
      top: header + margin,
    },
    // Upper-center — loading list (clear of header / bg)
    'loading-list': {
      left: clamp(w / 2 - 160, margin, w - 340),
      top: header + margin + 36,
    },
    // Right side mid — object attribute details
    'object-details': {
      left: clamp(w - detailsW - margin - 8, margin, w - detailsW - margin),
      top: header + margin + bgSize + margin + 8,
    },
    // Bottom-right — tools FAB
    'scene-fab': {
      left: clamp(w - fabSize - margin, margin, w - fabSize - margin),
      top: clamp(h - fabSize - margin - 8, header, h - fabSize - margin),
    },
    // Above FAB, to its left — measure strip
    'measure-toolbar': {
      left: clamp(w - fabSize - measureW - margin * 2, margin, w - measureW - margin),
      top: clamp(h - measureH - margin - 8, header, h - 80),
    },
    // Top-right — live scene performance
    'scene-data': {
      left: clamp(w - 332 - margin, margin, w - 332),
      top: header + margin,
    },
    // Bottom-left — hover info
    'scene-info': {
      left: margin,
      top: clamp(h - 78, header, h - 64),
    },
    // Above info — object material colour
    'object-color': {
      left: margin,
      top: clamp(h - 240, header + 80, h - 200),
    },
  };

  return map[id] || { left: margin, top: header + margin };
}
